import { useState, useCallback } from "react";

import { Form, Select } from "antd";
import { clsx } from "clsx";

import {
    RVFormFloatingLabelWrap,
    RVSelectCreate,
    generateLexicalDateTimeValidator,
    generateLexicalBooleanValidator,
    generateLexicalNumberValidator,
} from "@regal-voice/ui";
import {
    generateFormItemRequiredRules,
    generateJsonValidator,
} from "Components/FormItems/Shared/Validators/InputValidators/InputValidators";
import { createSchemaOption } from "Components/shared/RVEventSchemaOption/RVEventSchemaOption";
import {
    UpdateContactLexicalInput,
    HANDLEBAR_TRIGGERS,
} from "Pages/orchestration/Pages/journeys/JourneyEditPage/Components/JourneyBuilder/Components/JourneyActionsPanel/NodeEditorPanel/Components/UpdateContactFormItems/Components/UpdateContactFormPanelProfile/Components/UpdateProfileFormItems/Components/UpdateContactLexicalInput/UpdateContactLexicalInput";

import {
    FIELD_TYPE_STRING,
    FIELD_TYPE_NUMBER,
    FIELD_TYPE_BOOLEAN,
    FIELD_TYPE_OBJECT,
    FIELD_TYPE_DATE,
    FIELD_TYPE_LIST,
    FIELD_TYPE_NEW_CUSTOM,
} from "../../../../constants";

import styles from "./UpdateContactAttributeFormPanel.module.scss";

const customOptions = [
    {
        label: "String",
        value: FIELD_TYPE_STRING,
    },
    {
        label: "Number",
        value: FIELD_TYPE_NUMBER,
    },
    {
        label: "Boolean",
        value: FIELD_TYPE_BOOLEAN,
    },
    {
        label: "Datetime",
        value: FIELD_TYPE_DATE,
    },
    {
        label: "Object",
        value: FIELD_TYPE_OBJECT,
    },
    {
        label: "List",
        value: FIELD_TYPE_LIST,
    },
];

// todo move this to the correct place and fix the import
const lexicalDateValidator = generateLexicalDateTimeValidator("Date format is invalid.", HANDLEBAR_TRIGGERS);
const JSONValidator = generateJsonValidator("JSON is not in a valid format");

const booleanValidator = generateLexicalBooleanValidator("Boolean value is invalid.", HANDLEBAR_TRIGGERS);
const numberValidator = generateLexicalNumberValidator("Number format is invalid.", HANDLEBAR_TRIGGERS);

const VALUE_CUSTOM_SELECT_RULES = generateFormItemRequiredRules("Data Type");
const VALUE_STRING_RULES = generateFormItemRequiredRules("Input value");
const VALUE_NUMBER_RULES = [...generateFormItemRequiredRules("Number value"), numberValidator];
const VALUE_DATE_RULES = [...generateFormItemRequiredRules("Date value"), lexicalDateValidator];
const VALUE_BOOLEAN_RULES = [...generateFormItemRequiredRules("Boolean value"), booleanValidator];
const VALUE_OBJECT_RULES = [
    ...generateFormItemRequiredRules("Object value"),
    {
        validator: JSONValidator,
    },
];

//find a good shared place for this
export interface OptionsData extends Array<any> {
    value?: string;
}

type UpdateContactAttributeFormPanelProps = {
    attrNameLabel: string;
    attrNameFormName: Array<string>;
    attrTypeLabel: string;
    attrTypeFormName: Array<string>;
    attrValueFormName: Array<string>;
    createNewText: string;
    originalOptions: OptionsData;
    createNewPrefix: string;
    handleFieldChange: (value: string, isCustomAttr: boolean) => void;
    handleValueChange: (value: string) => void;
    handleTypeChange: (value: string) => void;
    getValueLabel: (fieldType: string) => string;
};

const selectRulesForInputType = (type: string) => {
    switch (type) {
        case FIELD_TYPE_NUMBER:
            return VALUE_NUMBER_RULES;
        case FIELD_TYPE_STRING:
            return VALUE_STRING_RULES;
        case FIELD_TYPE_BOOLEAN:
            return VALUE_BOOLEAN_RULES;
        case FIELD_TYPE_DATE:
            return VALUE_DATE_RULES;
        case FIELD_TYPE_OBJECT:
            return VALUE_OBJECT_RULES;
        case FIELD_TYPE_LIST:
            return VALUE_OBJECT_RULES;
        default:
            return [];
    }
};

export function UpdateContactAttributeFormPanel({
    attrNameLabel,
    attrNameFormName,
    attrTypeLabel,
    attrTypeFormName,
    attrValueFormName,
    createNewText,
    originalOptions,
    createNewPrefix,
    handleFieldChange,
    handleValueChange,
    handleTypeChange,
    getValueLabel,
}: UpdateContactAttributeFormPanelProps) {
    const [eventOptions, setEventOptions] = useState<OptionsData>(originalOptions);

    const [isNewCustomAttr, setIsNewCustomAttr] = useState(false);

    const fieldTypeWatch = Form.useWatch(attrTypeFormName);

    const currentSchema = Form.useWatch(attrNameFormName);

    const isCustomField = fieldTypeWatch === FIELD_TYPE_NEW_CUSTOM;
    const hasField = fieldTypeWatch && fieldTypeWatch.length > 0 && !isCustomField;
    const isDoubleLine = fieldTypeWatch === FIELD_TYPE_OBJECT || fieldTypeWatch === FIELD_TYPE_LIST;
    const lexicalLabel = getValueLabel(fieldTypeWatch);
    const lexicalInputRules = selectRulesForInputType(fieldTypeWatch);

    const handleSelectChange = useCallback(
        (value: string) => {
            let isCustomAttr = true;
            // if the value is not in the origal options, it is a custom attr
            if (originalOptions.some((option) => option.value === value)) {
                isCustomAttr = false;
            }
            handleFieldChange(value, isCustomAttr);
            setIsNewCustomAttr(isCustomAttr);
        },
        [handleFieldChange, originalOptions]
    );

    const onAttrSelect = useCallback(
        (attr: string) => {
            const customAttr = `${createNewPrefix}${attr}`;
            setEventOptions([
                createSchemaOption(customAttr, customAttr, FIELD_TYPE_NEW_CUSTOM, "", false),
                ...eventOptions,
            ]);
            handleSelectChange(customAttr);
        },
        [createNewPrefix, eventOptions, handleSelectChange]
    );

    return (
        <>
            <RVFormFloatingLabelWrap
                name={attrNameFormName}
                rules={generateFormItemRequiredRules(attrNameLabel)}
                label={attrNameLabel}
                validateFirst
            >
                <RVSelectCreate
                    options={eventOptions}
                    creationText={createNewText}
                    onCreate={onAttrSelect}
                    onSelect={handleSelectChange}
                />
            </RVFormFloatingLabelWrap>

            <div
                className={clsx(styles.customTypeSelect, {
                    [styles.customTypeSelectHidden]: !isNewCustomAttr,
                })}
            >
                <RVFormFloatingLabelWrap
                    label={attrTypeLabel}
                    hidden={!isNewCustomAttr}
                    name={attrTypeFormName}
                    rules={VALUE_CUSTOM_SELECT_RULES}
                >
                    <Select options={customOptions} onSelect={handleTypeChange}></Select>
                </RVFormFloatingLabelWrap>
            </div>

            {hasField && (
                <UpdateContactLexicalInput
                    onChange={handleValueChange}
                    currentSchema={currentSchema}
                    isSingleLine={!isDoubleLine}
                    fieldType={fieldTypeWatch}
                    name={attrValueFormName}
                    rules={lexicalInputRules}
                    label={lexicalLabel}
                ></UpdateContactLexicalInput>
            )}
        </>
    );
}
